import api from './api';

export interface Player {
  id: string;
  name: string;
  nickname?: string;
  birthDate?: string;
  position?: string;
  jerseyNumber?: number;
  notes?: string;
  isActive: boolean;
  parentId: string;
  email?: string; // For Gravatar and contact
  profilePhotoType?: 'UPLOAD' | 'GRAVATAR';
  profilePhotoUrl?: string;
  playerColor?: string;
  createdAt: string;
  updatedAt: string;
  
  // Home address fields
  homeAddressStreet?: string;
  homeAddressCity?: string;
  homeAddressState?: string;
  homeAddressZip?: string;
  homeAddressValidated?: boolean;
  homeAddressValidatedAt?: string;
  homeAddressConfidence?: string;
  homeAddressFormatted?: string;
  
  leagueAssignments: PlayerLeagueAssignment[];
  tournamentAssignments: PlayerTournamentAssignment[];
}

export interface PlayerLeagueAssignment {
  id: string;
  teamName: string;
  teamId?: string;
  teamLogoPath?: string;
  division?: string;
  season?: string;
  jerseyNumber?: number;
  position?: string;
  isStarter?: boolean;
  notes?: string;
  platformType: 'GOTSPORT' | 'SINCSPORTS' | 'MANUAL';
  platformTeamId?: string;
  platformUrl?: string;
  active: boolean;
  league: {
    id: string;
    name: string;
    division?: string;
    logoUrl?: string;
    logoPath?: string;
    standingsSortBy?: string;
    standingsSource?: string;
  };
  club?: {
    id: string;
    name: string;
    logoUrl?: string;
  };
}

export interface PlayerTournamentAssignment {
  id: string;
  teamName: string;
  division?: string;
  jerseyNumber?: number;
  position?: string;
  isStarter?: boolean;
  notes?: string;
  platformType: 'GOTSPORT' | 'SINCSPORTS' | 'MANUAL';
  platformTeamId?: string;
  platformUrl?: string;
  active: boolean;
  tournament: {
    id: string;
    name: string;
    logoUrl?: string;
  };
  club?: {
    id: string;
    name: string;
    logoUrl?: string;
  };
}

export interface CreatePlayerRequest {
  name: string;
  nickname?: string | null;
  birthDate?: string | null;
  birthMonth?: number;
  birthYear?: number;
  position?: string | null;
  jerseyNumber?: number | null;
  notes?: string | null;
  isActive?: boolean;
  
  // Home address fields
  homeAddressStreet?: string | null;
  homeAddressCity?: string | null;
  homeAddressState?: string | null;
  homeAddressZip?: string | null;
  playerColor?: string | null;
}

export interface UpdatePlayerRequest {
  name?: string;
  nickname?: string | null;
  email?: string | null;
  birthDate?: string | null;
  position?: string | null;
  jerseyNumber?: number | null;
  notes?: string | null;
  isActive?: boolean;
  
  // Home address fields
  homeAddressStreet?: string | null;
  homeAddressCity?: string | null;
  homeAddressState?: string | null;
  homeAddressZip?: string | null;
  playerColor?: string | null;
}

export interface UpdateHomeAddressRequest {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export interface UpdateHomeAddressResponse {
  player: {
    id: string;
    homeAddressStreet?: string;
    homeAddressCity?: string;
    homeAddressState?: string;
    homeAddressZip?: string;
    homeAddressIsApproximate: boolean;
  };
  isSufficient: boolean;
  message?: string;
  precision?: 'precise' | 'approximate' | 'rough';
}

export interface DepartureTimeInfo {
  matchId: string;
  departureTime: string; // ISO 8601 format
  isApproximate: boolean;
  travelTimeMinutes: number;
  baseTravelTimeMinutes?: number;
  bufferMinutes?: number;
  warmupMinutes?: number;
  origin: {
    type: 'home' | 'previous_match' | 'other_player_match';
    location: string;
    playerId?: string;
    latitude?: number;
    longitude?: number;
  };
}

export interface AssignLeagueRequest {
  leagueId: string;
  teamName: string;
  clubId?: string;
  division?: string;
  season?: string;
  jerseyNumber?: number | null;
  position?: string | null;
  isStarter?: boolean;
  notes?: string;
}

export interface Match {
  id: string;
  homeTeam: string;
  awayTeam: string;
  dateTime: string;
  location?: string;
  locationConfirmed: boolean;
  status: string;
  matchType: string;
  homeScore?: number;
  awayScore?: number;
  league: {
    id: string;
    name: string;
  };
  homeClub?: {
    id: string;
    name: string;
    logoUrl?: string;
  };
  awayClub?: {
    id: string;
    name: string;
    logoUrl?: string;
  };
}

export const playerService = {
  async getPlayers(): Promise<Player[]> {
    const response = await api.get('/players');
    return response.data.players;
  },

  async getPlayer(id: string): Promise<Player> {
    const response = await api.get(`/players/${id}`);
    return response.data.player;
  },

  async createPlayer(data: CreatePlayerRequest): Promise<Player> {
    const response = await api.post('/players', data);
    return response.data.player;
  },

  async updatePlayer(id: string, data: UpdatePlayerRequest): Promise<{ player: Player; addressFeedback?: any }> {
    const response = await api.put(`/players/${id}`, data);
    return response.data;
  },

  async deletePlayer(id: string): Promise<void> {
    await api.delete(`/players/${id}`);
  },

  async assignToLeague(playerId: string, data: AssignLeagueRequest): Promise<PlayerLeagueAssignment> {
    const response = await api.post(`/players/${playerId}/leagues`, data);
    return response.data.assignment;
  },

  async updateAssignment(playerId: string, assignmentId: string, data: AssignLeagueRequest): Promise<PlayerLeagueAssignment> {
    const response = await api.put(`/players/${playerId}/leagues/${assignmentId}`, data);
    return response.data.assignment;
  },

  async removeFromLeague(playerId: string, assignmentId: string): Promise<void> {
    await api.delete(`/players/${playerId}/leagues/${assignmentId}`);
  },

  async changeTeam(
    playerId: string,
    assignmentId: string,
    data: { teamId: string; teamName: string }
  ): Promise<PlayerLeagueAssignment> {
    const response = await api.put(
      `/players/${playerId}/league-assignments/${assignmentId}/change-team`,
      data
    );
    return response.data.data;
  },

  async getPlayerSchedule(playerId: string, startDate?: string, endDate?: string): Promise<Match[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await api.get(`/players/${playerId}/schedule?${params.toString()}`);
    return response.data.matches;
  },

  async updateHomeAddress(playerId: string, data: UpdateHomeAddressRequest): Promise<UpdateHomeAddressResponse> {
    const response = await api.put(`/players/${playerId}/home-address`, data);
    return response.data;
  },

  async getDepartureTimes(playerId: string, startDate?: string, endDate?: string): Promise<DepartureTimeInfo[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await api.get(`/players/${playerId}/departure-times?${params.toString()}`);
    return response.data.departureTimes || [];
  },
};
